import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { t } from 'i18next';
import client from '../../service/graphql';
import { IGraphQLResponse } from '../../types/commonTypes';
import {
  ICoachCreateAccount,
  ICreateCoach,
  IVerifyEmail,
  onboardingActions,
} from './onboardingSlice';
import {
  COACH_ONBOARDING_MUTATION,
  UPDATE_COACH_PROFILE_PICTURE,
} from '../../service/graphql/coachQuery';
import { toastStyles } from '../../utils/utils';
import { EMAIL_VERIFICATION_ROUTE, LOGIN_ROUTE } from '../../utils/routes';
import { logout } from '../GlobalAction';
import i18n from '../../i18n';
import { brandTagKey } from '../../config/constants';
import { ICoach } from '../coach/coachSlice';

interface IUploadCoachProfilePicResponse {
  updateCoachProfilePic: {
    profilePicLink: {
      contentType: string;
      fileName: string;
      url: string;
      key: string;
    };
  };
}

async function saveprofilePicInS3({
  fileObject,
  preSignedUrl,
}: {
  fileObject: File | null;
  preSignedUrl: string;
}): Promise<void> {
  if (fileObject?.name) {
    await axios.put(preSignedUrl, fileObject, {
      headers: {
        'Content-Type': `${fileObject.type}`,
      },
    });
  }
}

async function createCoach(payload: ICreateCoach): Promise<AxiosResponse> {
  return axios.post(`${process.env.REACT_APP_AUTH_SERVICE}/auth/user`, payload);
}

async function verifyEmail(payload: IVerifyEmail): Promise<void> {
  await axios.post(`${process.env.REACT_APP_AUTH_SERVICE}/auth/verifyEmail`, payload);
}

async function resendVerifyCode(payload: { coachId: string }): Promise<void> {
  await axios.post(`${process.env.REACT_APP_AUTH_SERVICE}/auth/resend-verification`, payload);
}

function* watchCreateCoach({
  payload,
}: PayloadAction<{
  data: ICreateCoach;
  navigate: NavigateFunction;
}>) {
  try {
    const result = yield call(createCoach, payload.data);
    yield put(
      onboardingActions.createCoachSuccess({ ...result.data, coachTypes: [], therapyTypes: [] })
    );
    const brandTag = localStorage.getItem(brandTagKey);
    if (brandTag) {
      payload.navigate(`${EMAIL_VERIFICATION_ROUTE}/${brandTag}`);
    } else {
      payload.navigate(EMAIL_VERIFICATION_ROUTE);
    }
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      let message = error.response?.data.message;
      if (error.response?.data.message === t('therapistOnboarding.validations.userAlreadyExists')) {
        message = t('therapistOnboarding.validations.emailAlreadyExists');
      }
      toast.error(message, toastStyles.error);
      yield put(onboardingActions.createCoachFailure(error.response?.data.message));
    }
  }
}

function* watchVerifyEmail({
  payload,
}: PayloadAction<{ data: IVerifyEmail; navigate: NavigateFunction }>) {
  try {
    yield call(verifyEmail, payload.data);
    yield put(logout());
    const brandTag = localStorage.getItem(brandTagKey);
    if (brandTag) {
      payload.navigate(`${LOGIN_ROUTE}/${brandTag}`, { replace: true });
    } else {
      payload.navigate(LOGIN_ROUTE, { replace: true });
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(onboardingActions.verifyEmailFailure(error.message));
    }
  }
}

function* watchCreateCoachAccount({ payload }: PayloadAction<{ data: ICoachCreateAccount }>) {
  try {
    const { CoachOnboarding, profilePic } = payload.data;
    if (profilePic) {
      const result: IGraphQLResponse<IUploadCoachProfilePicResponse> = yield call(
        client.mutate.bind(client),
        {
          mutation: UPDATE_COACH_PROFILE_PICTURE,
          variables: {
            id: CoachOnboarding.id,
            fileName: profilePic.name,
            contentType: profilePic.type,
          },
        }
      );

      const payloadObj = {
        fileObject: profilePic,
        preSignedUrl: result.data.updateCoachProfilePic.profilePicLink.url,
      };
      yield call(saveprofilePicInS3, payloadObj);
      CoachOnboarding.profilePic = result.data.updateCoachProfilePic.profilePicLink.key;
    }

    const coachOnboardRes: IGraphQLResponse<{ coachOnboarding: ICoach }> = yield call(
      client.mutate.bind(client),
      {
        mutation: COACH_ONBOARDING_MUTATION,
        variables: { CoachOnboarding },
      }
    );
    yield put(onboardingActions.createCoachAccountSuccess(coachOnboardRes.data.coachOnboarding));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(onboardingActions.createCoachAccountFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchResendVerificationCode({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    yield call(resendVerifyCode, payload);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(onboardingActions.resendVerificationCodeFailure(error.message));
    }
  }
}

export function* watchOnboardingSagas() {
  yield takeLatest(onboardingActions.createCoach, watchCreateCoach);
  yield takeLatest(onboardingActions.verifyEmail, watchVerifyEmail);
  yield takeLatest(onboardingActions.createCoachAccount, watchCreateCoachAccount);
  yield takeLatest(onboardingActions.resendVerificationCode, watchResendVerificationCode);
}

const onboardingSaga = watchOnboardingSagas;
export default onboardingSaga;
